import React from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

// Loading Spinner Variants
export type SpinnerSize = 'sm' | 'md' | 'lg' | 'xl';
export type SpinnerVariant = 'primary' | 'secondary' | 'muted' | 'accent';

interface LoadingSpinnerProps {
  size?: SpinnerSize;
  variant?: SpinnerVariant;
  className?: string;
  text?: string;
}

// Size configurations
const SPINNER_SIZES: Record<SpinnerSize, string> = {
  sm: 'w-4 h-4',
  md: 'w-6 h-6', 
  lg: 'w-8 h-8',
  xl: 'w-12 h-12'
};

// Variant configurations  
const SPINNER_VARIANTS: Record<SpinnerVariant, string> = {
  primary: 'text-primary',
  secondary: 'text-secondary',
  muted: 'text-muted-foreground', 
  accent: 'text-accent'
};

// Universal Loading Spinner
export const LoadingSpinner = ({ 
  size = 'md', 
  variant = 'primary', 
  className,
  text 
}: LoadingSpinnerProps) => {
  return (
    <div className={cn('flex flex-col items-center justify-center gap-3', className)}>
      <Loader2 
        className={cn(
          'animate-spin',
          SPINNER_SIZES[size],
          SPINNER_VARIANTS[variant]
        )} 
      />
      {text && (
        <p className="text-sm text-muted-foreground font-medium">
          {text}
        </p>
      )}
    </div>
  );
};

// Inline Loading Spinner for buttons/small spaces
export const InlineSpinner = ({ 
  size = 'sm', 
  variant = 'primary',
  className 
}: Omit<LoadingSpinnerProps, 'text'>) => {
  return (
    <Loader2 
      className={cn(
        'animate-spin',
        SPINNER_SIZES[size],
        SPINNER_VARIANTS[variant],
        className
      )} 
    />
  );
};

// Page Loading States
interface PageLoadingProps {
  title?: string;
  subtitle?: string;
  variant?: 'page' | 'admin' | 'minimal';
}

export const PageLoading = ({ 
  title = "Loading...", 
  subtitle,
  variant = 'page' 
}: PageLoadingProps) => {
  if (variant === 'minimal') {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <LoadingSpinner size="lg" text={title} />
      </div>
    );
  }

  if (variant === 'admin') {
    return (
      <div className="min-h-screen bg-background">
        {/* Admin Header Skeleton */}
        <div className="h-16 border-b bg-background">
          <div className="container mx-auto px-4 h-full flex items-center">
            <Skeleton className="h-8 w-48" />
          </div>
        </div>
        
        {/* Admin Navigation Skeleton */}
        <div className="h-12 border-b bg-muted/20">
          <div className="container mx-auto px-4 h-full flex items-center gap-6">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-6 w-20" />
            ))}
          </div>
        </div>
        
        {/* Admin Content Loading */}
        <div className="container mx-auto px-4 py-8">
          <div className="text-center mb-8">
            <LoadingSpinner size="lg" text={title} />
            {subtitle && (
              <p className="text-muted-foreground mt-3">{subtitle}</p>
            )}
          </div>
          
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 9 }).map((_, i) => (
              <AdminCardSkeleton key={i} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Default page loading
  return (
    <div className="min-h-screen bg-background">
      {/* Navigation skeleton */}
      <div className="h-20 border-b bg-background/80 backdrop-blur-xl">
        <div className="container mx-auto px-4 h-full flex items-center justify-between">
          <Skeleton className="h-8 w-32" />
          <div className="flex gap-6">
            <Skeleton className="h-6 w-20" />
            <Skeleton className="h-6 w-20" />
            <Skeleton className="h-6 w-20" />
            <Skeleton className="h-6 w-24" />
          </div>
        </div>
      </div>

      {/* Main content loading */}
      <div className="container mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <LoadingSpinner size="xl" text={title} />
          {subtitle && (
            <p className="text-muted-foreground mt-4 text-lg">{subtitle}</p>
          )}
        </div>
        
        <div className="space-y-8">
          <Skeleton className="h-12 w-64 mx-auto" />
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <ProductCardSkeleton key={i} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Specialized Skeleton Components
export const ProductCardSkeleton = ({ className }: { className?: string }) => (
  <div className={cn('space-y-4 p-4 border border-border rounded-lg', className)}>
    <Skeleton className="aspect-square w-full rounded-md" />
    <div className="space-y-2">
      <Skeleton className="h-5 w-3/4" />
      <Skeleton className="h-4 w-1/2" />
      <div className="flex justify-between items-center">
        <Skeleton className="h-6 w-16" />
        <Skeleton className="h-8 w-20" />
      </div>
    </div>
    <div className="flex gap-2">
      <Skeleton className="h-9 flex-1" />
      <Skeleton className="h-9 w-9" />
    </div>
  </div>
);

export const AdminCardSkeleton = ({ className }: { className?: string }) => (
  <div className={cn('space-y-4 p-6 border border-border rounded-lg', className)}>
    <div className="flex items-center gap-3">
      <Skeleton className="h-8 w-8 rounded-full" />
      <Skeleton className="h-5 w-3/4" />
    </div>
    <Skeleton className="h-4 w-full" />
    <Skeleton className="h-4 w-2/3" />
    <div className="flex gap-2 pt-2">
      <Skeleton className="h-8 w-16" />
      <Skeleton className="h-8 w-20" />
    </div>
  </div>
);

export const ListItemSkeleton = ({ className }: { className?: string }) => (
  <div className={cn('flex items-center gap-4 p-4', className)}>
    <Skeleton className="h-12 w-12 rounded-full flex-shrink-0" />
    <div className="flex-1 space-y-2">
      <Skeleton className="h-4 w-3/4" />
      <Skeleton className="h-3 w-1/2" />
    </div>
    <Skeleton className="h-8 w-16" />
  </div>
);

export const TableSkeleton = ({ 
  rows = 5, 
  columns = 4,
  className 
}: { 
  rows?: number; 
  columns?: number;
  className?: string;
}) => (
  <div className={cn('w-full space-y-4', className)}>
    {/* Table Header */}
    <div className="flex gap-4 pb-2 border-b">
      {Array.from({ length: columns }).map((_, i) => (
        <Skeleton key={i} className="h-4 flex-1" />
      ))}
    </div>
    
    {/* Table Rows */}
    {Array.from({ length: rows }).map((_, rowIndex) => (
      <div key={rowIndex} className="flex gap-4 py-2">
        {Array.from({ length: columns }).map((_, colIndex) => (
          <Skeleton key={colIndex} className="h-4 flex-1" />
        ))}
      </div>
    ))}
  </div>
);

// Content Loading States
export const ContentLoading = ({ lines = 3 }: { lines?: number }) => (
  <div className="space-y-3">
    {Array.from({ length: lines }).map((_, i) => (
      <Skeleton 
        key={i} 
        className={cn(
          'h-4',
          i === lines - 1 ? 'w-2/3' : 'w-full'
        )} 
      />
    ))}
  </div>
);

// Button Loading State
export const ButtonLoading = ({ 
  children, 
  isLoading, 
  loadingText,
  spinnerSize = 'sm',
  className 
}: {
  children: React.ReactNode;
  isLoading: boolean;
  loadingText?: string;
  spinnerSize?: SpinnerSize;
  className?: string;
}) => (
  <span className={cn('flex items-center gap-2', className)}>
    {isLoading && <InlineSpinner size={spinnerSize} />}
    {isLoading ? loadingText || 'Loading...' : children}
  </span>
);